import React, { useEffect, useState } from "react";
import * as moment from "moment";
import {
  Button,
  TextField,
  Grid,
  Tooltip,
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import Pagination from "@mui/material/Pagination";
import BackupRoundedIcon from "@mui/icons-material/BackupRounded";
import DateMonthPicker from "../../../../components/common/DateMonthPicker";
import ORBApi from "../../../../services/ORBApi";
import Loading from "../../../../components/common/Loading";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import excelLogo from "../../../../assets/excel-logo.png";
import "../../../../utils/date";
import { getFormatByBackofficeSource } from "../../../../utils/ReportSourceUtils";
import ProgressBar from "../../../../components/common/ProgressBar";

export function AddFilePanelBackoffice(props) {
  /** {onSave} */

  const [selectedFile, setSelectedFile] = useState(null);
  const [shouldValidateForm, setShouldValidateForm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [checked, setChecked] = useState(false);
  const [uploadedReportId, setUploadedReportId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const [sourceModels, setSourceModels] = useState([]);
  const [selectedSourceModelIds, setSelectedSourceModelIds] = useState([]);
  const [loadingSourceModels, setLoadingSourceModels] = useState(false);
  const [sourceModelsLoadFailed, setSourceModelsLoadFailed] = useState(false);

  const [reportData, setReportData] = useState({
    month: "",
    name: "",
    currency: "",
    report_source: "",
    comma_as_decimal_sep: "",
    show_exchange_rate: false,
  });

  const OptionsBackoffice = [
    // { value: 1, label: "Youtube" },
    { value: 2, label: "Backoffice" },
    // { value: 3, label: "Deezer BMAT" },
  ];

  const SortOptionsBackoffice = OptionsBackoffice.sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const validateForm = () => {
    setShouldValidateForm(true);

    if (
      !selectedFile ||
      reportData.month === "" ||
      reportData.name === "" ||
      reportData.currency === "" ||
      reportData.report_source === "" ||
      (reportData.comma_as_decimal_sep !== 0 &&
        reportData.comma_as_decimal_sep !== 1 &&
        reportData.comma_as_decimal_sep !== "0" &&
        reportData.comma_as_decimal_sep !== "1")
    ) {
      return false;
    }

    if (sourceModelsLoadFailed) {
      toast.error(
        "Não foi possível carregar os modelos de relatório. Tente novamente."
      );
      return false;
    }

    if (sourceModels.length > 0 && selectedSourceModelIds.length === 0) {
      toast.error("Selecione pelo menos um modelo de relatório.");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (SortOptionsBackoffice.length === 1) {
      setReportData((prev) => {
        if (!prev.report_source) {
          const selectedSource = SortOptionsBackoffice[0].value;
          const autoFormat = getFormatByBackofficeSource(selectedSource);

          return {
            ...prev,
            report_source: selectedSource,
            comma_as_decimal_sep: autoFormat,
          };
        }

        return prev;
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSourceModels = async () => {
      setSelectedSourceModelIds([]);
      setSourceModels([]);
      setSourceModelsLoadFailed(false);

      if (!reportData.report_source) {
        return;
      }

      setLoadingSourceModels(true);

      try {
        const data = await ORBApi._doGet(
          `/private/payment_report_source_models?rights_type=WORK&source_id=${reportData.report_source}&active=true`
        );

        if (cancelled) {
          return;
        }

        const models = Array.isArray(data?.models) ? data.models : [];

        const filteredModels = models.filter(
          (model) =>
            model &&
            model.active === true &&
            model.rights_type === "WORK" &&
            Number(model.source_id) === Number(reportData.report_source)
        );

        setSourceModels(filteredModels);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("Erro ao carregar modelos de relatório:", error);
        setSourceModelsLoadFailed(true);
        setSourceModels([]);
        setSelectedSourceModelIds([]);
        toast.error("Erro ao carregar os modelos de relatório da fonte.");
      } finally {
        if (!cancelled) {
          setLoadingSourceModels(false);
        }
      }
    };

    loadSourceModels();

    return () => {
      cancelled = true;
    };
  }, [reportData.report_source]);

  const toggleSourceModel = (modelId) => {
    setSelectedSourceModelIds((current) => {
      if (current.includes(modelId)) {
        return current.filter((id) => id !== modelId);
      }

      return [...current, modelId];
    });
  };

  const startProgressPolling = (reportId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    fetchProgress(reportId);

    const interval = setInterval(() => {
      fetchProgress(reportId);
    }, 2000);

    setPollingInterval(interval);
  };

  const fetchProgress = async (reportId) => {
    try {
      const data = await ORBApi.getWorkPaymentReportStatus(reportId);
      setProgressData(data);

      if (data.status === 2 || data.status === 3) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }

        if (data.status === 3) {
          props.onSave(true);
          setProcessing(false);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar progresso:", error);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      props.onSave(true);
      setProcessing(false);
    }
  };

  const handleProgressComplete = () => {
    props.onSave(true);
    setProcessing(false);
  };

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const uploadForm = () => {
    if (!validateForm()) return;

    setProcessing(true);

    const form = new FormData();

    form.append("report_file", selectedFile);
    form.append("month", moment(reportData.month).format("YYYY-MM"));
    form.append("name", reportData.name);
    form.append("report_source", reportData.report_source);
    form.append("currency", reportData.currency);
    form.append("comma_as_decimal_sep", reportData.comma_as_decimal_sep);
    form.append(
      "show_exchange_rate",
      reportData.show_exchange_rate === true ? 0 : 1
    );

    if (selectedSourceModelIds.length > 0) {
      form.append(
        "source_model_ids",
        JSON.stringify(selectedSourceModelIds)
      );
    }

    ORBApi.postWorkPaymentReports(form)
      .then((response) => {
        const reportId = response?.id || response?.result?.id;

        if (reportId) {
          setUploadedReportId(reportId);
          startProgressPolling(reportId);
        } else {
          setTimeout(() => {
            ORBApi.loadPaymentWorksReports(1, {})
              .then((data) => {
                if (
                  data.work_payment_files &&
                  data.work_payment_files.length > 0
                ) {
                  const latestReport = data.work_payment_files[0];

                  if (latestReport && latestReport.id) {
                    setUploadedReportId(latestReport.id);
                    startProgressPolling(latestReport.id);
                  } else {
                    props.onSave(true);
                    setProcessing(false);
                  }
                } else {
                  props.onSave(true);
                  setProcessing(false);
                }
              })
              .catch(() => {
                props.onSave(true);
                setProcessing(false);
              });
          }, 1000);
        }
      })
      .catch((error) => {
        console.error("Erro ao enviar relatório autoral:", error);

        const apiMessage =
          error?.response?.data?.description ||
          error?.response?.data?.message;

        if (apiMessage) {
          toast.error(apiMessage);
        }

        props.onSave(false);
        setProcessing(false);
      });
  };

  return (
    <>
      <h4 className="title"> Carregar arquivo autoral</h4>
      <hr />

      <Grid container spacing={2} alignItems="center">
        <Grid item md={4} xs={12}>
          <TextField
            style={{ width: "100%", paddingTop: "8px" }}
            variant="outlined"
            required
            label="Nome"
            error={shouldValidateForm && !reportData.name ? true : null}
            onChange={(e) => {
              setReportData({ ...reportData, name: e.target.value });
            }}
          />
        </Grid>

        <Grid
          item
          md={4}
          xs={12}
          style={{ display: "flex", alignItems: "flex-start" }}
        >
          <div style={{ width: "100%" }}>
            <DateMonthPicker
              label="Mês Referência"
              error={shouldValidateForm && !reportData.month}
              format="MM/yyyy"
              view={["month", "year"]}
              required
              value={reportData.month}
              onChange={(month) => {
                setReportData({ ...reportData, month: month });
              }}
            />
          </div>
        </Grid>

        <Grid item md={4} xs={12}>
          <FormControl
            style={{ width: "100%", paddingTop: "8px" }}
            className="form-select"
            required
            error={shouldValidateForm && !reportData.currency ? true : null}
          >
            <InputLabel className="select-label">Moeda</InputLabel>
            <Select
              native
              label="Moeda"
              value={reportData.currency}
              onChange={(e) =>
                setReportData({ ...reportData, currency: e.target.value })
              }
              variant="outlined"
            >
              <option value="" />
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="BRL">BRL</option>
            </Select>
          </FormControl>
        </Grid>

        <Grid item md={4} xs={12}>
          <FormControl
            style={{ width: "100%" }}
            required
            error={
              shouldValidateForm && !reportData.report_source ? true : null
            }
          >
            <InputLabel className="select-label"> Fonte </InputLabel>
            <Select
              native
              label="Fonte"
              value={reportData.report_source}
              onChange={(e) => {
                const selectedSource = e.target.value;
                const autoFormat = getFormatByBackofficeSource(selectedSource);

                setReportData({
                  ...reportData,
                  report_source: selectedSource,
                  comma_as_decimal_sep: autoFormat,
                });
              }}
              variant="outlined"
            >
              <option aria-label="None" value="" />
              {SortOptionsBackoffice.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item md={3} xs={12}>
          <FormControl
            style={{ width: "100%" }}
            required
            error={
              shouldValidateForm &&
              reportData.comma_as_decimal_sep !== 0 &&
              reportData.comma_as_decimal_sep !== 1 &&
              reportData.comma_as_decimal_sep !== "0" &&
              reportData.comma_as_decimal_sep !== "1"
                ? true
                : null
            }
          >
            <InputLabel className="select-label"> Formato </InputLabel>
            <Select
              native
              label="Formato"
              value={reportData.comma_as_decimal_sep}
              disabled
              variant="outlined"
            >
              <option aria-label="None" value="" />
              <option value={0}>Americano (".")</option>
              <option value={1}>Brasileiro (",")</option>
            </Select>
          </FormControl>
        </Grid>

        <Grid item md={3} xs={6}>
          <div
            className="form-item-container clickable"
            style={{ width: "100%", cursor: "pointer" }}
            onClick={() => {
              document.getElementById("real-file").click();
            }}
          >
            <input
              id="real-file"
              type="file"
              accept=".csv,.xls,.xlsx"
              hidden="hidden"
              onChange={(e) =>
                setSelectedFile(
                  e.target.files && e.target.files.length > 0
                    ? e.target.files[0]
                    : null
                )
              }
            />

            <BackupRoundedIcon
              className="upload-icon"
              fontSize="large"
              color="secondary"
            />

            <span
              className={
                !selectedFile && shouldValidateForm
                  ? "error panel-options-texts"
                  : "panel-options-texts"
              }
            >
              {selectedFile ? selectedFile.name : "Selecionar arquivo..."}
            </span>
          </div>
        </Grid>

        <Grid item md={2} xs={12}>
          <FormControlLabel
            style={{ marginLeft: "-40px" }}
            control={
              <Checkbox
                checked={checked}
                onChange={() => {
                  const newChecked = !checked;

                  setReportData({
                    ...reportData,
                    show_exchange_rate: newChecked,
                  });

                  setChecked(newChecked);
                }}
              />
            }
            label={
              <Typography variant="body1" style={{ fontSize: ".750rem" }}>
                Esconder taxas de câmbio
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <div
            style={{
              border: "1px solid rgba(0, 0, 0, 0.23)",
              borderRadius: "4px",
              padding: "12px 16px",
              minHeight: "72px",
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: 500,
                marginBottom: "6px",
              }}
            >
              Modelos de relatório
              {sourceModels.length > 0 ? " *" : ""}
            </Typography>

            {!reportData.report_source ? (
              <Typography variant="body2" color="textSecondary">
                Selecione uma fonte para carregar os modelos disponíveis.
              </Typography>
            ) : loadingSourceModels ? (
              <Loading size="24px" />
            ) : sourceModelsLoadFailed ? (
              <Typography variant="body2" color="error">
                Não foi possível carregar os modelos desta fonte.
              </Typography>
            ) : sourceModels.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                Nenhum modelo dinâmico ativo para esta fonte. O processamento
                legado será utilizado.
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: "4px" }}
                >
                  Selecione uma ou mais abas/modelos que devem processar este
                  arquivo.
                </Typography>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px 18px",
                  }}
                >
                  {sourceModels.map((model) => (
                    <FormControlLabel
                      key={model.id}
                      control={
                        <Checkbox
                          checked={selectedSourceModelIds.includes(model.id)}
                          onChange={() => toggleSourceModel(model.id)}
                          color="secondary"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {model.name}
                          {model.sheet_name
                            ? ` — aba: ${model.sheet_name}`
                            : ""}
                          {model.version
                            ? ` (v${model.version})`
                            : ""}
                        </Typography>
                      }
                    />
                  ))}
                </div>

                {shouldValidateForm &&
                  selectedSourceModelIds.length === 0 && (
                    <Typography variant="body2" color="error">
                      Selecione pelo menos um modelo de relatório.
                    </Typography>
                  )}
              </>
            )}
          </div>
        </Grid>

        <Grid item xs={12}>
          <div
            className="form-item-container"
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "16px",
            }}
          >
            {processing ? (
              <Loading />
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={uploadForm}
                size="large"
                style={{ minWidth: "300px" }}
                disabled={loadingSourceModels}
              >
                Enviar
              </Button>
            )}
          </div>
        </Grid>

        {progressData && (
          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <div style={{ width: "100%", maxWidth: "600px" }}>
                <ProgressBar
                  processedRows={progressData.processed_rows || 0}
                  totalRows={progressData.total_rows || 0}
                  progressPercent={progressData.progress_percent || 0}
                  status={
                    progressData.status === 2
                      ? "LOADED"
                      : progressData.status === 3
                      ? "ERROR"
                      : "LOADING"
                  }
                  onComplete={handleProgressComplete}
                />
              </div>
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export function DeletionConfirmPanel({ report, onConfirm }) {
  const [isProcessing, setIsProcessing] = useState(false);

  const doDeleteFile = () => {
    setIsProcessing(true);
    ORBApi.deletePaymentWorkReports(report.id)
      .then(() => {
        onConfirm(true);
      })
      .catch(() => {
        onConfirm(false);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <>
      <h5>Deseja realmente remover este arquivo?</h5>
      <div className="deletion-subtitle">
        ({report.month} {report.source})
      </div>
      {isProcessing ? (
        <div className="loading-container">
          <Loading />{" "}
        </div>
      ) : (
        <div className="action-button-container">
          <Button variant="contained" color="secondary" onClick={doDeleteFile}>
            Confirmar
          </Button>
        </div>
      )}
    </>
  );
}

export function UpdateStatusConfirmPanel({
  report_id,
  loadData,
  current_page,
  setStatusVisible,
}) {
  const [loading, setLoading] = useState(0);

  const updateStatus = () => {
    let body = { status: 4 };
    setLoading(1);
    ORBApi.updateWorkPaymentDetails(report_id, body)
      .then(() => {
        toast.success("Status atualizado !");
        loadData(current_page);
      })
      .catch((error) => {
        toast.error("Erro ao atualizar status.");
      })
      .finally(() => {
        setLoading(0);
        setStatusVisible(false);
      });
  };

  return (
    <>
      <h5>Deseja atualizar o status do arquivo?</h5>
      <div className="action-button-container">
        {loading ? (
          <Loading size="24px" />
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => updateStatus()}
          >
            Confirmar
          </Button>
        )}
      </div>
    </>
  );
}

export function NotFoundWorksPanel(props) {
  /** {report, onDownload} */

  const [worksData, setWorksData] = useState({
    works: [],
    pages: 0,
    current_page: 0,
    total_items: 0,
  });
  const [downloading, setDownloading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);

  const downloadExcel = () => {
    setDownloading(true);
    ORBApi.downloadWorkPaymentReportNotFoundWorks(props.report.id)
      .then((data) => {
        const file = new Blob([data], { type: `application/excel` });
        let link = document.createElement("a");
        link.href = window.URL.createObjectURL(file);
        link.download = `ORB-not-found-works.xls`;
        link.click();
      })
      .then(() => {
        props.onDownload(true);
      })
      .catch(() => {
        props.onDownload(false);
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const onPageChangeHandler = (page) => {
    loadReports(page);
  };

  const rebuildNotFoundWorks = () => {
    setRebuilding(true);
    ORBApi.rebuildWorkPaymentReportNotFoundWorks(props.report.id)
      .then(() => {
        loadReports(1);
      })
      .finally(() => {
        setRebuilding(false);
      });
  };

  const loadReports = (page) => {
    ORBApi.loadWorkPaymentReportNotFoundWorks(props.report.id, page).then(
      (data) => {
        setWorksData(data);
      }
    );
  };

  useEffect(() => {
    loadReports(1);
  }, []);

  return (
    <>
      <div className="update-logo">
        {rebuilding ? (
          <Loading size="25px" className="" />
        ) : (
          <Tooltip title="Atualiza lista de obras não encontradas.">
            <UpdateRoundedIcon
              className="clickable"
              fontSize="large"
              color="secondary"
              onClick={rebuildNotFoundWorks}
            />
          </Tooltip>
        )}
      </div>
      <div className="excel-logo">
        {downloading ? (
          <Loading size="25px" className="download-loading" />
        ) : (
          <Tooltip title="Baixa a lista de obras não encontradas em Excel.">
            <img
              src={excelLogo}
              className="clickable"
              onClick={downloadExcel}
              alt="Excel"
            />
          </Tooltip>
        )}
      </div>
      <TableContainer className="base-table">
        <Table>
          <TableHead className="header">
            <TableRow>
              <TableCell>Código ORB</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Autor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {worksData
              ? worksData.works.map((work) => {
                  return (
                    <TableRow key={work.orb_code}>
                      <TableCell>{work.orb_code}</TableCell>
                      <TableCell>{work.title}</TableCell>
                      <TableCell>{work.author}</TableCell>
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        shape="rounded"
        className="table-pagination"
        count={worksData.pages}
        page={worksData.current_page}
        onChange={(e, value) => {
          onPageChangeHandler(value);
        }}
      />
    </>
  );
}
